/**
 * Scraper Domain Model
 *
 * Encapsulates business logic and validation rules for scrapers.
 * Pure business logic - no Firestore operations.
 */

export class Scraper {
  constructor(data = {}) {
    // Core identification
    this.id = data.id || null;
    this.scraperId = data.scraperId || data.id || null; // Unique identifier
    this.name = data.name || null;

    // Platform information
    this.platform = data.platform || null; // e.g., 'Railway', 'VPS'
    this.url = data.url || null;

    // Status
    this.availability =
      data.availability !== undefined ? data.availability : true;
    // Retired hardware kept for provenance, not for use.
    //
    // ⚠️ Distinct from `availability`, which is an OPERATIONAL flag: the two
    // VPS boxes are unavailable because the browser cannot reach them (they
    // are firewalled, and the tool is HTTPS so http://ip:3000 is mixed
    // content), yet they still claim and run jobs. `legacy` means the opposite
    // — decommissioned, of historical interest only. Conflating them would
    // hide a working scraper or imply a dead one still runs.
    this.legacy = data.legacy === true;
    this.status = data.status || "unknown"; // unknown, healthy, degraded, down

    // Health information
    this.lastHealthCheck = data.lastHealthCheck || null;
    this.healthDetails = data.healthDetails || null;
    this.proxyHealth = data.proxyHealth || null;

    // Timestamps
    this.createdAt = data.createdAt || null;
    this.updatedAt = data.updatedAt || null;
    this.lastUsed = data.lastUsed || null;
  }

  /**
   * Validate scraper data
   * @returns {Object} - { valid: boolean, errors: string[] }
   */
  validate() {
    const errors = [];

    if (!this.name || this.name.trim() === "") {
      errors.push("Scraper name is required");
    }

    if (!this.scraperId || this.scraperId.trim() === "") {
      errors.push("Scraper ID is required");
    }

    if (!this.platform || this.platform.trim() === "") {
      errors.push("Platform is required");
    }

    if (this.url && !this.isValidUrl(this.url)) {
      errors.push("Invalid URL format");
    }

    if (!["unknown", "healthy", "degraded", "down"].includes(this.status)) {
      errors.push("Invalid status value");
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Check if scraper can be deleted
   * @param {number} scrapeCount - Number of associated scrapes
   * @returns {Object} - { canDelete: boolean, reason: string }
   */
  canBeDeleted(scrapeCount = 0) {
    // Scrapers can always be deleted, but warn about orphaned scrapes
    return {
      canDelete: true,
      reason:
        scrapeCount > 0
          ? `This will orphan ${scrapeCount} scrape(s). The scrapes will remain but won't be linked to a scraper.`
          : null,
    };
  }

  /**
   * Check if scraper is healthy
   * @returns {boolean}
   */
  isHealthy() {
    return this.status === "healthy" && this.availability === true;
  }

  /**
   * Check if scraper is available for use
   * @returns {boolean}
   */
  isAvailable() {
    return this.availability === true;
  }

  /**
   * Get human-readable status
   * @returns {string}
   */
  getStatusLabel() {
    if (!this.availability) return "Disabled";

    switch (this.status) {
      case "healthy":
        return "Healthy";
      case "degraded":
        return "Degraded";
      case "down":
        return "Down";
      default:
        return "Unknown";
    }
  }

  /**
   * Get status badge variant
   * @returns {string}
   */
  getStatusBadgeVariant() {
    if (!this.availability) return "secondary";

    switch (this.status) {
      case "healthy":
        return "success";
      case "degraded":
        return "warning";
      case "down":
        return "danger";
      default:
        return "info";
    }
  }

  /**
   * Validate URL format
   * @param {string} url - URL to validate
   * @returns {boolean}
   */
  isValidUrl(url) {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Convert to Firestore format
   * @returns {Object} - Firestore document data
   */
  toFirestore() {
    return {
      scraperId: this.scraperId,
      name: this.name,
      platform: this.platform,
      url: this.url,
      availability: this.availability,
      legacy: this.legacy,
      status: this.status,
      lastHealthCheck: this.lastHealthCheck,
      healthDetails: this.healthDetails,
      proxyHealth: this.proxyHealth,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      lastUsed: this.lastUsed,
    };
  }

  /**
   * Create from Firestore document
   * @param {DocumentSnapshot} doc - Firestore document snapshot
   * @returns {Scraper} - Scraper instance
   */
  static fromFirestore(doc) {
    if (!doc.exists()) return null;

    const data = doc.data();
    return new Scraper({
      id: doc.id,
      ...data,
      // Convert Firestore Timestamps to Date objects
      createdAt: data.createdAt?.toDate?.() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
      lastUsed: data.lastUsed?.toDate?.() || data.lastUsed,
      lastHealthCheck: data.lastHealthCheck?.toDate?.() || data.lastHealthCheck,
    });
  }

  /**
   * Create a new scraper instance with default values
   * @param {Object} data - Initial data
   * @returns {Scraper}
   */
  static create(data = {}) {
    return new Scraper({
      ...data,
      status: data.status || "unknown",
      availability: data.availability !== undefined ? data.availability : true,
      legacy: data.legacy === true,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
  }
}
